/**
 * 投资组合图表组件
 */

import React, { useEffect, useRef } from 'react';
import { Card, Spin, Alert, Row, Col, Statistic, Tabs } from 'antd';
import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { PortfolioChartProps } from './types';

const { TabPane } = Tabs;

const PortfolioChart: React.FC<PortfolioChartProps> = ({
  data,
  chartType = 'allocation',
  loading = false,
  error,
  config,
  onDataPointClick,
  onZoomChange,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || loading || error || !data.length) return;

    // 初始化图表
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    let option: EChartsOption = {};

    switch (chartType) {
      case 'allocation':
        // 资产配置饼图
        const allocationData = data.map(item => ({
          name: item.symbol,
          value: item.weight,
          itemStyle: {
            color: item.color || '#1890ff'
          }
        }));

        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '资产配置',
            left: 'center',
            top: 20,
            textStyle: {
              fontSize: 16,
              fontWeight: 'bold'
            }
          },
          tooltip: {
            trigger: 'item',
            formatter: '{a} <br/>{b}: {c}% ({d}%)'
          },
          legend: {
            orient: 'vertical',
            left: 'left',
            data: allocationData.map(item => item.name)
          },
          series: [
            {
              name: '资产配置',
              type: 'pie',
              radius: ['40%', '70%'],
              center: ['50%', '60%'],
              avoidLabelOverlap: false,
              label: {
                show: false,
                position: 'center'
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: '18',
                  fontWeight: 'bold'
                }
              },
              labelLine: {
                show: false
              },
              data: allocationData
            }
          ]
        };
        break;

      case 'performance':
        // 投资组合表现对比
        const symbols = [...new Set(data.map(item => item.symbol))];
        const dates = [...new Set(data.map(item => item.timestamp).filter((timestamp): timestamp is string => timestamp !== undefined))]
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        
        const series = symbols.map(symbol => {
          const symbolData = data.filter(item => item.symbol === symbol);
          return {
            name: symbol,
            type: 'line' as const,
            data: dates.map(date => {
              const item = symbolData.find(d => d.timestamp === date);
              return item ? item.value : null;
            }),
            smooth: true,
            lineStyle: { width: 2 }
          };
        });

        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '投资组合表现',
            left: 'center'
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'cross'
            }
          },
          legend: {
            data: symbols,
            top: 30
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: dates
          },
          yAxis: {
            type: 'value',
            axisLabel: {
              formatter: '{value}%'
            }
          },
          series
        };
        break;

      case 'correlation':
        // 相关性热力图
        const correlationSymbols = [...new Set(data.map(item => item.symbol))];
        const correlationData: number[][] = [];
        
        correlationSymbols.forEach((symbol1, i) => {
          correlationSymbols.forEach((symbol2, j) => {
            const correlation = data.find(item => 
              item.symbol === symbol1 && item.correlationWith === symbol2
            )?.correlation || (i === j ? 1 : 0);
            correlationData.push([i, j, correlation]);
          });
        });

        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '资产相关性矩阵',
            left: 'center'
          },
          tooltip: {
            position: 'top',
            formatter: (params: any) => {
              const [x, y, value] = params.data;
              return `${correlationSymbols[x]} vs ${correlationSymbols[y]}<br/>相关性: ${value.toFixed(3)}`;
            }
          },
          grid: {
            height: '50%',
            top: '10%'
          },
          xAxis: {
            type: 'category',
            data: correlationSymbols,
            splitArea: {
              show: true
            }
          },
          yAxis: {
            type: 'category',
            data: correlationSymbols,
            splitArea: {
              show: true
            }
          },
          visualMap: {
            min: -1,
            max: 1,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: '15%',
            inRange: {
              color: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', 
                     '#ffffcc', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026']
            }
          },
          series: [{
            name: '相关性',
            type: 'heatmap',
            data: correlationData,
            label: {
              show: true,
              formatter: (params: any) => params.data[2].toFixed(2)
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowColor: 'rgba(0, 0, 0, 0.5)'
              }
            }
          }]
        };
        break;

      default:
        option = {
          backgroundColor: config?.theme?.backgroundColor || 'transparent',
          title: {
            text: '投资组合分析',
            left: 'center'
          },
          series: []
        };
    }

    chartInstance.current.setOption(option);

    // 绑定事件
    if (onDataPointClick) {
      chartInstance.current.on('click', (params: any) => {
        if (params.data) {
          onDataPointClick(params.data);
        }
      });
    }

    if (onZoomChange) {
      chartInstance.current.on('datazoom', (params: any) => {
        const startIndex = Math.floor((params.start / 100) * data.length);
        const endIndex = Math.floor((params.end / 100) * data.length);
        if (data[startIndex] && data[endIndex]) {
          // 如果数据有timestamp，使用timestamp；否则使用索引
          const startTime = data[startIndex].timestamp || startIndex.toString();
          const endTime = data[endIndex].timestamp || endIndex.toString();
          onZoomChange(startTime, endTime);
        }
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [data, chartType, config, loading, error, onDataPointClick, onZoomChange]);

  // 计算投资组合统计指标
  const calculatePortfolioMetrics = () => {
    if (!data.length) return {};

    const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);
    const totalWeight = data.reduce((sum, item) => sum + (item.weight || 0), 0);
    const avgReturn = data.reduce((sum, item) => sum + (item.return || 0), 0) / data.length;
    const avgRisk = data.reduce((sum, item) => sum + (item.risk || 0), 0) / data.length;

    return {
      totalValue,
      totalWeight,
      avgReturn,
      avgRisk,
      sharpeRatio: avgRisk > 0 ? avgReturn / avgRisk : 0,
      assetCount: new Set(data.map(item => item.symbol)).size
    };
  };

  const metrics = calculatePortfolioMetrics();

  if (error) {
    return <Alert message="图表加载失败" description={error} type="error" showIcon />;
  }

  return (
    <Card title="投资组合分析" loading={loading}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Statistic
            title="总价值"
            value={metrics.totalValue || 0}
            precision={2}
            prefix="¥"
            valueStyle={{ color: '#1890ff' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="平均收益率"
            value={metrics.avgReturn || 0}
            precision={2}
            suffix="%"
            valueStyle={{ 
              color: (metrics.avgReturn || 0) >= 0 ? '#52c41a' : '#ff4d4f' 
            }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="夏普比率"
            value={metrics.sharpeRatio || 0}
            precision={3}
            valueStyle={{ color: '#faad14' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="资产数量"
            value={metrics.assetCount || 0}
            valueStyle={{ color: '#722ed1' }}
          />
        </Col>
      </Row>

      <Tabs defaultActiveKey="allocation" onChange={(key) => {
        // 这里可以触发图表类型切换
        console.log('切换到:', key);
      }}>
        <TabPane tab="资产配置" key="allocation">
          <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
        </TabPane>
        <TabPane tab="表现对比" key="performance">
          <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
        </TabPane>
        <TabPane tab="相关性分析" key="correlation">
          <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default PortfolioChart;